import { DialogComponent } from '@theme/dialog';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Stages a native Shopify option value inside a dialog. The native input is
 * activated only after confirmation, so closing the dialog never changes the
 * selected variant.
 */
class StagedVariantOptionPicker extends DialogComponent {
  /** @type {string} */
  #confirmedOptionValueId = '';
  /** @type {string} */
  #selectedOptionValueId = '';
  /** @type {HTMLElement | null} */
  #previouslyFocusedElement = null;
  /** @type {AbortController | null} */
  #mobileSheetAbortController = null;
  /** @type {AbortController | null} */
  #mobileSheetDragAbortController = null;
  /** @type {AbortController | null} */
  #productSelectAbortController = null;
  #dialogRequested = false;
  #mobileSheetExpanded = false;
  #suppressSheetToggle = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:close', this.#restoreFocus);
    this.#confirmedOptionValueId = this.#checkedInput?.dataset.optionValueId || '';
    this.#selectedOptionValueId = this.#confirmedOptionValueId;
    this.#syncTriggerSwatch();
    this.#connectMobileSheet();
    this.#productSelectAbortController?.abort();
    this.#productSelectAbortController = new AbortController();
    this.closest('variant-picker')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#productSelectAbortController.signal,
    });
  }

  #reconcileSelection() {
    const checkedInput = this.#checkedInput;
    const fallbackOptionValue = this.dataset.fallbackOptionValue;
    const checkedInputAvailable =
      checkedInput && !checkedInput.disabled && checkedInput.dataset.optionAvailable === 'true';

    if (!checkedInputAvailable && fallbackOptionValue) {
      const fallbackInput = Array.from(
        this.querySelectorAll('[data-staged-option-inputs] input:not(:disabled)')
      ).find(
        (input) =>
          input instanceof HTMLInputElement &&
          input.value === fallbackOptionValue &&
          input.dataset.optionAvailable === 'true'
      );

      if (fallbackInput instanceof HTMLInputElement) {
        const fallbackOptionValueId = fallbackInput.dataset.optionValueId || '';
        this.#confirmedOptionValueId = fallbackOptionValueId;
        this.#selectedOptionValueId = fallbackOptionValueId;

        if (this.refs.dialog.open || this.#dialogRequested) this.#syncSelection();
        this.#syncTriggerSwatch();
        if (!fallbackInput.checked) fallbackInput.click();
        return;
      }
    }

    const confirmedOptionValueId = checkedInput?.dataset.optionValueId;
    if (confirmedOptionValueId) {
      this.#confirmedOptionValueId = confirmedOptionValueId;
      this.#selectedOptionValueId = confirmedOptionValueId;
    }

    // A variant morph replaces the rendered detail placeholder while preserving an open dialog.
    if (this.refs.dialog.open || this.#dialogRequested) this.#syncSelection();
    this.#syncTriggerSwatch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:close', this.#restoreFocus);
    this.#productSelectAbortController?.abort();
    this.#mobileSheetAbortController?.abort();
    this.#mobileSheetDragAbortController?.abort();
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    event.promise
      .then(() => this.#reconcileSelection())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[staged-variant-option-picker] Event promise rejected:', error);
      });
  };

  showDialog() {
    if (this.refs.dialog.open || this.#dialogRequested) return;

    this.#dialogRequested = true;
    this.#previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#confirmedOptionValueId = this.#checkedInput?.dataset.optionValueId || '';
    this.#selectedOptionValueId = this.#confirmedOptionValueId;
    this.#syncSelection();
    this.#setMobileSheetExpanded(false);
    super.showDialog();

    requestAnimationFrame(() => {
      this.#dialogRequested = false;
      const closeButton = this.querySelector('[ref="closeButton"]');
      if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });

      for (const scroller of this.querySelectorAll('[data-staged-option-scroll-start]')) {
        scroller.scrollTop = 0;
      }
    });
  }

  /** @param {MouseEvent} event */
  selectValue(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.dataset.optionAvailable !== 'true') return;

    this.#selectedOptionValueId = button.dataset.optionValueId || '';
    this.#syncSelection(button, true);
    this.#setMobileSheetExpanded(true);
  }

  toggleMobileSheet() {
    if (this.#suppressSheetToggle) {
      this.#suppressSheetToggle = false;
      return;
    }

    this.#setMobileSheetExpanded(!this.#mobileSheetExpanded);
  }

  async confirmSelection() {
    const input = this.querySelector(
      `[data-staged-option-inputs] [data-option-value-id="${CSS.escape(this.#selectedOptionValueId)}"]`
    );

    if (!(input instanceof HTMLInputElement)) return;

    const changed = !input.checked;
    await this.closeDialog();
    if (changed) input.click();
  }

  /**
   * @param {HTMLButtonElement} [selectedButton]
   * @param {boolean} [scrollToDetail=false]
   */
  #syncSelection(selectedButton, scrollToDetail = false) {
    const choiceButtons = this.querySelectorAll('[data-staged-option-choice]');

    choiceButtons.forEach((button) => {
      const isSelected = button.getAttribute('data-option-value-id') === this.#selectedOptionValueId;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    const button =
      selectedButton ||
      this.querySelector(
        `[data-staged-option-choice][data-option-value-id="${CSS.escape(this.#selectedOptionValueId)}"]`
      );
    if (!(button instanceof HTMLButtonElement)) return;

    const selectionLabel = this.querySelector('[ref="selectionLabel"]');
    if (selectionLabel) selectionLabel.textContent = button.dataset.optionLabel || '';

    const selectionPrice = this.querySelector('[ref="selectionPrice"]');
    if (selectionPrice && button.dataset.previewPrice) {
      selectionPrice.textContent = button.dataset.previewPrice;
    }

    const previewImage = this.querySelector('[ref="previewImage"]');
    const previewSrc = button.dataset.previewSrc;
    if (previewImage instanceof HTMLImageElement && previewSrc) {
      previewImage.src = previewSrc;
      previewImage.removeAttribute('srcset');
    }
    const detail = this.querySelector('[data-staged-option-detail]');
    const template = this.querySelector(
      `[data-staged-option-detail-template][data-option-value-id="${CSS.escape(this.#selectedOptionValueId)}"]`
    );
    if (detail instanceof HTMLElement) {
      detail.replaceChildren();
      if (template instanceof HTMLTemplateElement) detail.append(template.content.cloneNode(true));

      // Choice data refreshes with each variant morph, but template contents retain their initial values.
      const detailPrice = detail.querySelector('[data-staged-option-detail-price]');
      if (detailPrice instanceof HTMLElement) {
        const previewPrice = button.dataset.previewPrice?.trim() || '';
        detailPrice.textContent = previewPrice;
        detailPrice.hidden = previewPrice === '';
      }

      detail.hidden = !(template instanceof HTMLTemplateElement);
    }

    const confirmButton = this.querySelector('[data-staged-option-confirm]');
    if (confirmButton instanceof HTMLButtonElement) {
      confirmButton.disabled =
        this.dataset.disableConfirmUnchanged === 'true' &&
        this.#selectedOptionValueId === this.#confirmedOptionValueId;
    }

    if (scrollToDetail && detail instanceof HTMLElement && window.matchMedia('(max-width: 749px)').matches) {
      requestAnimationFrame(() => {
        detail.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          block: 'start',
        });
      });
    }
  }

  /** Uses the selected option image when present, retaining the Liquid product-image fallback otherwise. */
  #syncTriggerSwatch() {
    const selectedButton = this.querySelector(
      `[data-staged-option-choice][data-option-value-id="${CSS.escape(this.#selectedOptionValueId)}"]`
    );
    const triggerSwatch = this.querySelector('.grouped-option__trigger > .swatch');
    const swatchSrc = selectedButton instanceof HTMLElement ? selectedButton.dataset.triggerSwatchSrc : '';

    if (!(triggerSwatch instanceof HTMLElement) || !swatchSrc) return;
    triggerSwatch.style.setProperty('--swatch-background', `url("${swatchSrc}")`);
    triggerSwatch.classList.add('swatch--variant-image');
  }

  /** @returns {HTMLInputElement | null} */
  get #checkedInput() {
    const input = this.querySelector('[data-staged-option-inputs] input:checked');
    return input instanceof HTMLInputElement ? input : null;
  }

  #connectMobileSheet() {
    const sheet = this.querySelector('[data-mobile-preview-sheet]');
    const handle = this.querySelector('[data-mobile-preview-sheet-handle]');
    const catalog = this.querySelector('[data-mobile-preview-sheet-catalog]');
    if (!(sheet instanceof HTMLElement) || !(handle instanceof HTMLElement)) return;

    this.#mobileSheetAbortController?.abort();
    this.#mobileSheetAbortController = new AbortController();
    const { signal } = this.#mobileSheetAbortController;

    catalog?.addEventListener('scroll', this.#collapseMobileSheet, { passive: true, signal });
    handle.addEventListener('pointerdown', this.#startMobileSheetDrag, { signal });
  }

  /** @param {boolean} expanded */
  #setMobileSheetExpanded(expanded) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const sheet = this.querySelector('[data-mobile-preview-sheet]');
    const toggle = this.querySelector('[data-mobile-preview-sheet-handle]');
    if (!(sheet instanceof HTMLElement) || !isMobile) return;

    this.#mobileSheetExpanded = expanded;
    this.classList.toggle('is-mobile-sheet-expanded', expanded);
    sheet.dataset.state = expanded ? 'expanded' : 'collapsed';
    sheet.style.removeProperty('--mobile-sheet-drag-offset');
    toggle?.setAttribute('aria-expanded', String(expanded));
  }

  #collapseMobileSheet = () => {
    if (this.#mobileSheetExpanded) this.#setMobileSheetExpanded(false);
  };

  /** @param {PointerEvent} event */
  #startMobileSheetDrag = (event) => {
    if (!(event instanceof PointerEvent) || !event.isPrimary || event.button !== 0) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const sheet = this.querySelector('[data-mobile-preview-sheet]');
    if (!(sheet instanceof HTMLElement)) return;

    const startY = event.clientY;
    let dragDistance = 0;
    this.#mobileSheetDragAbortController?.abort();
    this.#mobileSheetDragAbortController = new AbortController();
    const { signal } = this.#mobileSheetDragAbortController;

    /** @param {PointerEvent} moveEvent */
    const move = (moveEvent) => {
      if (!(moveEvent instanceof PointerEvent) || moveEvent.pointerId !== event.pointerId) return;
      dragDistance = moveEvent.clientY - startY;
      if (this.#mobileSheetExpanded) {
        sheet.style.setProperty('--mobile-sheet-drag-offset', `${Math.max(0, dragDistance)}px`);
      }
    };

    /** @param {PointerEvent} finishEvent */
    const finish = (finishEvent) => {
      if (!(finishEvent instanceof PointerEvent) || finishEvent.pointerId !== event.pointerId) return;
      this.#mobileSheetDragAbortController?.abort();
      sheet.style.removeProperty('--mobile-sheet-drag-offset');

      if (Math.abs(dragDistance) > 8) {
        this.#suppressSheetToggle = true;
        setTimeout(() => {
          this.#suppressSheetToggle = false;
        });
      }
      if (dragDistance >= 48) this.#setMobileSheetExpanded(false);
      if (dragDistance <= -48) this.#setMobileSheetExpanded(true);
    };

    window.addEventListener('pointermove', move, { passive: true, signal });
    window.addEventListener('pointerup', finish, { signal });
    window.addEventListener('pointercancel', finish, { signal });
  };

  #restoreFocus = () => {
    this.#dialogRequested = false;
    if (this.#previouslyFocusedElement?.isConnected) {
      this.#previouslyFocusedElement.focus({ preventScroll: true });
    }
    this.#previouslyFocusedElement = null;
  };
}

if (!customElements.get('staged-variant-option-picker')) {
  customElements.define('staged-variant-option-picker', StagedVariantOptionPicker);
}
